// StaffPaymentHelpers.jsx
// All reusable sub-components extracted from StaffPaymentTable

import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { HiSearch, HiX } from "react-icons/hi";
import { FiChevronDown, FiArrowUpRight, FiArrowDownLeft } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const BASE_URL = process.env.REACT_APP_API_URL;
const STAFF_PER_PAGE = 10;

// -----------------------------------------------------------------------------
// StaffDropdown
// -----------------------------------------------------------------------------
export const StaffDropdown = ({ value, onChange, onClear }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const fetchStaff = useCallback(async (searchTerm = "", pageNum = 1) => {
    setLoading(true);
    try {
      const token = loadDecrypted("HeartConnect-Token");
      const { data } = await axios.get(`${BASE_URL}/staff/get-all-staff`, {
        params: { search: searchTerm, page: pageNum, limit: STAFF_PER_PAGE },
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      if (data.success) {
        setStaffList((prev) =>
          pageNum === 1 ? data.data : [...prev, ...data.data]
        );
        setTotalPages(data.pagination?.totalPages ?? 1);
      }
    } catch (err) {
      console.error("Failed to fetch staff", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = () => {
    if (!open) {
      setOpen(true);
      setSearch("");
      setPage(1);
      fetchStaff("", 1);
    } else {
      setOpen(false);
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchStaff(val, 1);
    }, 350);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchStaff(search, nextPage);
  };

  const handleSelect = (staff) => {
    setSelectedStaff(staff);
    onChange(staff.id || staff._id, staff);
    setOpen(false);
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    setSelectedStaff(null);
    setSearch("");
    onChange("", null);
    if (onClear) onClear();
  };

  return (
    <div className="relative w-full sm:max-w-xs" ref={dropdownRef}>
      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-400">
        Select Staff
      </label>
      <button
        type="button"
        onClick={handleOpen}
        className="relative flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-3 text-sm text-gray-700 outline-none transition focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
      >
        <span
          className={
            selectedStaff ? "font-medium text-gray-800" : "text-gray-400"
          }
        >
          {selectedStaff
            ? `${selectedStaff.fullName} — ${selectedStaff.phoneNumber}`
            : "Select a Staff"}
        </span>
        <span className="ml-2 flex shrink-0 items-center gap-1">
          {selectedStaff && (
            <span
              onClick={handleClear}
              className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-gray-400 hover:text-[#c51369]"
            >
              <HiX size={11} />
            </span>
          )}
          <FiChevronDown
            size={14}
            className={`text-gray-400 transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-72 rounded-xl border border-gray-100 bg-white shadow-xl">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <HiSearch className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#c51369]" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by name or phone…"
                className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-3 text-xs text-gray-700 outline-none focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                    fetchStaff("", 1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#c51369]"
                >
                  <HiX size={11} />
                </button>
              )}
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto">
            {staffList.length === 0 && !loading && (
              <li className="px-3 py-4 text-center text-xs text-gray-400">
                No staff found.
              </li>
            )}
            {staffList.map((s) => {
              const sid = s.id || s._id;
              const isSelected = value === sid;
              return (
                <li
                  key={sid}
                  onClick={() => handleSelect(s)}
                  className={`cursor-pointer px-3 py-2 transition-colors hover:bg-pink-50 ${
                    isSelected ? "bg-pink-50" : ""
                  }`}
                >
                  <p
                    className={`text-sm font-medium ${
                      isSelected ? "text-[#c51369]" : "text-gray-800"
                    }`}
                  >
                    {s.fullName}
                    {isSelected && (
                      <span className="ml-1.5 text-[10px] text-[#c51369]">
                        ✓
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">{s.phoneNumber}</p>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-gray-100 px-3 py-2 text-center">
            {loading ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                <AiOutlineLoading3Quarters
                  size={12}
                  className="animate-spin text-[#c51369]"
                />
                Loading…
              </span>
            ) : page < totalPages ? (
              <button
                onClick={handleLoadMore}
                className="text-xs font-semibold text-[#c51369] hover:underline"
              >
                Load more
              </button>
            ) : (
              staffList.length > 0 && (
                <span className="text-xs text-gray-300">
                  {staffList.length} staff loaded
                </span>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// TypeBadge
// -----------------------------------------------------------------------------
export const TypeBadge = ({ type }) =>
  type === "credit" ? (
    <span className="bg-emerald-50 text-emerald-700 inline-flex items-center gap-1 rounded-full bg-green-200 px-2.5 py-0.5 text-xs font-semibold text-green-600">
      <FiArrowDownLeft size={11} /> Credit
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-600">
      <FiArrowUpRight size={11} /> Debit
    </span>
  );

// -----------------------------------------------------------------------------
// SummaryCards
// -----------------------------------------------------------------------------
export const SummaryCards = ({
  isAdmin,
  todayTotalCount,
  todayTotalCredit,
  todayTotalDebit,
}) => (
  <div
    className={`mb-6 grid gap-3 ${
      isAdmin ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-3"
    }`}
  >
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
        Today's Total Transactions
      </p>
      <p className="mt-1 text-2xl font-bold text-gray-800">{todayTotalCount}</p>
    </div>

    {!isAdmin && (
      <div className="border-emerald-100 bg-emerald-50 rounded-xl border p-4 shadow-sm">
        <p className="text-emerald-600 text-[11px] font-bold uppercase tracking-wider">
          Today's Total Credit
        </p>
        <p className="text-emerald-700 mt-1 text-xl font-bold">
          ₹{todayTotalCredit.toLocaleString("en-IN")}
        </p>
      </div>
    )}

    <div className="rounded-xl border border-red-100 bg-red-50 p-4 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-wider text-red-600">
        Today's Total Debit
      </p>
      <p className="mt-1 text-xl font-bold text-red-700">
        ₹{todayTotalDebit.toLocaleString("en-IN")}
      </p>
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// TransactionFilters
// -----------------------------------------------------------------------------
export const TransactionFilters = ({
  isAdmin,
  search,
  setSearch,
  dateFilter,
  setDateFilter,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  txnTypeFilter,
  setTxnTypeFilter,
  walletStatusFilter,
  setWalletStatusFilter,
  onResetPage,
  onClearFilters,
}) => (
  <div className="mb-6 rounded-2xl bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-end gap-3">
      {/* Search */}
      <div className="relative min-w-[180px] flex-1">
        <HiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#c51369]" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onResetPage();
          }}
          placeholder="Search.."
          className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-4 text-sm text-gray-700 outline-none focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
        />
      </div>

      {/* Date Filter */}
      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-400">
          Date
        </label>
        <div className="relative">
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              onResetPage();
            }}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
          >
            <option value="all">All Time</option>
            <option value="this_month">This Month</option>
            <option value="this_week">This Week</option>
            <option value="custom">Custom Range</option>
          </select>
          <FiChevronDown
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            size={14}
          />
        </div>
      </div>

      {/* Custom Date Range */}
      {dateFilter === "custom" && (
        <>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                onResetPage();
              }}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                onResetPage();
              }}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
            />
          </div>
        </>
      )}

      {/* Transaction Type (non-admin only) */}
      {!isAdmin && (
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-400">
            Transaction Type
          </label>
          <div className="relative">
            <select
              value={txnTypeFilter}
              onChange={(e) => {
                setTxnTypeFilter(e.target.value);
                onResetPage();
              }}
              className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
            >
              <option value="all">All</option>
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>
            <FiChevronDown
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              size={14}
            />
          </div>
        </div>
      )}

      {/* Wallet Status (admin only) */}
      {isAdmin && (
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-400">
            Wallet Status
          </label>
          <div className="relative">
            <select
              value={walletStatusFilter}
              onChange={(e) => {
                setWalletStatusFilter(e.target.value);
                onResetPage();
              }}
              className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
            >
              <option value="all">All</option>
              <option value="frozen">Frozen</option>
              <option value="unfrozen">Unfrozen</option>
            </select>
            <FiChevronDown
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              size={14}
            />
          </div>
        </div>
      )}

      {/* Clear */}
      <button
        onClick={onClearFilters}
        className="flex items-center gap-1 self-end rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 transition hover:border-[#c51369] hover:text-[#c51369]"
      >
        <HiX size={13} /> Clear
      </button>
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// TransactionTable (Desktop)
// -----------------------------------------------------------------------------
export const TransactionTable = ({
  loading,
  filteredTransactions,
  currentPage,
  PER_PAGE,
}) => (
  <div className="hidden overflow-x-auto rounded-xl shadow-sm md:block">
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="bg-[#e0ced7]">
          {["SL No.", "Date", "Staff", "Description", "Type", "Amount"].map(
            (h) => (
              <th
                key={h}
                className="px-5 py-3.5 text-center text-[11px] font-bold uppercase tracking-widest"
                style={{ color: "#9b6b85" }}
              >
                {h}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={6} className="py-16 text-center">
              <AiOutlineLoading3Quarters className="mx-auto h-6 w-6 animate-spin text-[#c51369]" />
              <p className="mt-2 text-sm text-gray-400">
                Loading transactions…
              </p>
            </td>
          </tr>
        ) : filteredTransactions.length === 0 ? (
          <tr>
            <td colSpan={6} className="py-14 text-center text-sm text-gray-400">
              No transactions found.
            </td>
          </tr>
        ) : (
          filteredTransactions.map((row, idx) => {
            const slNo = (currentPage - 1) * PER_PAGE + idx + 1;
            return (
              <tr
                key={row._id || idx}
                className="border-t border-pink-50 bg-white transition-colors hover:bg-pink-50/40"
              >
                <td className="px-5 py-4 text-center text-sm text-gray-600">
                  {slNo}
                </td>
                <td className="px-5 py-4 text-center">
                  <p className="text-sm font-semibold text-gray-800">
                    {row.displayDate}
                  </p>
                  <p className="mt-0.5 text-xs text-[#c51369]">
                    {row.displayTime}
                  </p>
                </td>
                <td className="px-5 py-4 text-center text-sm font-semibold text-gray-700">
                  {row.staffId?.fullName ||
                    (row.historyType === "RECHARGE" ? "User Recharge" : "—")}
                </td>
                <td className="px-5 py-4 text-center text-sm text-gray-700">
                  {row.description}
                </td>
                <td className="px-5 py-4 text-center">
                  <TypeBadge type={row.type} />
                </td>
                <td
                  className={`text-md px-5 py-4 text-center font-bold ${
                    row.type === "credit" ? "text-green-500" : "text-red-600"
                  }`}
                >
                  {row.type === "credit" ? "+" : "-"}
                  {row.amount.toLocaleString("en-IN")} Coins
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </div>
);

// -----------------------------------------------------------------------------
// TransactionMobileCards
// -----------------------------------------------------------------------------
export const TransactionMobileCards = ({
  loading,
  filteredTransactions,
  currentPage,
  PER_PAGE,
}) => (
  <div className="space-y-3 md:hidden">
    {loading ? (
      <div className="flex flex-col items-center gap-2 py-16 text-gray-400">
        <AiOutlineLoading3Quarters className="h-6 w-6 animate-spin text-[#c51369]" />
        <span>Loading transactions…</span>
      </div>
    ) : filteredTransactions.length === 0 ? (
      <p className="py-10 text-center text-sm text-gray-400">
        No transactions found.
      </p>
    ) : (
      filteredTransactions.map((row, idx) => {
        const slNo = (currentPage - 1) * PER_PAGE + idx + 1;
        return (
          <div
            key={row._id || idx}
            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="mb-1 text-xs text-gray-500">#{slNo}</p>
                <p className="mt-1.5 text-sm font-semibold text-gray-800">
                  {row.description}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {row.staffId?.fullName ||
                    (row.historyType === "RECHARGE" ? "User Recharge" : "—")}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {row.displayDate} · {row.displayTime}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p
                  className={`text-base font-bold ${
                    row.type === "credit" ? "text-green-500" : "text-red-600"
                  }`}
                >
                  {row.type === "credit" ? "+" : "-"}
                  {row.amount.toLocaleString("en-IN")} Coins
                </p>
                <div className="mt-1 flex justify-end">
                  <TypeBadge type={row.type} />
                </div>
              </div>
            </div>
          </div>
        );
      })
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Utility helpers (exported for use in main component)
// -----------------------------------------------------------------------------
export const formatDateFromString = (dateTimeStr) => {
  if (!dateTimeStr) return { date: "—", time: "—" };
  const [datePart, timePart] = dateTimeStr.split(" ");
  if (!datePart) return { date: "—", time: "—" };
  const [year, month, day] = datePart.split("-");
  const date = `${day}/${month}/${year}`;
  const time = timePart ? timePart.slice(0, 5) : "—";
  return { date, time };
};

export const parseDate = (str) => {
  if (!str) return null;
  const [d, m, y] = str.split("/");
  return new Date(`${y}-${m}-${d}`);
};
